import {
    AccountSyncTimes,
    getSyncBreakdownBullets,
} from "@helpers/accountSyncTimes";
import { timeAgoInSentence } from "@helpers/date";

export enum AccountStatusType {
    DISABLED = "disabled",
    NEEDS_ATTENTION = "needs_attention",
    INCOMPLETE = "incomplete",
    IMPORTED = "imported",
    CONNECTED = "connected",
}

export interface AccountStatusTooltip {
    title?: string;
    content?: string;
    bullets: string[];
}

function withSyncedAt(
    label: string,
    syncedLabel: string,
    syncedAt: Date | null,
): string {
    return syncedAt
        ? `${label} · ${syncedLabel} ${timeAgoInSentence(syncedAt)}`
        : label;
}

function getHeadline({
    status,
    syncTimes,
}: {
    status: AccountStatusType;
    syncTimes: AccountSyncTimes;
}): string {
    switch (status) {
        case AccountStatusType.DISABLED:
            return "Archived";
        case AccountStatusType.INCOMPLETE:
            return "Finish Stripe setup to collect payments.";
        case AccountStatusType.NEEDS_ATTENTION:
            return withSyncedAt(
                "Disconnected",
                "Last synced",
                syncTimes.lastSyncedAt,
            );
        case AccountStatusType.IMPORTED:
            return withSyncedAt(
                "Imported",
                "Last statement imported",
                syncTimes.transactionsSyncedAt,
            );
        case AccountStatusType.CONNECTED:
            return withSyncedAt("Connected", "Synced", syncTimes.lastSyncedAt);
    }
}

const STATUSES_WITH_SYNC_BREAKDOWN: ReadonlySet<AccountStatusType> = new Set([
    AccountStatusType.CONNECTED,
    AccountStatusType.NEEDS_ATTENTION,
]);

export function getAccountStatusTooltip({
    status,
    syncTimes,
}: {
    status: AccountStatusType;
    syncTimes: AccountSyncTimes;
}): AccountStatusTooltip {
    const headline = getHeadline({ status, syncTimes });
    const bullets = STATUSES_WITH_SYNC_BREAKDOWN.has(status)
        ? getSyncBreakdownBullets(syncTimes)
        : [];

    return bullets.length > 0
        ? { title: headline, bullets }
        : { content: headline, bullets };
}
